import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, User, ArrowRight, Quote, TrendingUp, Award, Target } from 'lucide-react';
import omOssImage from '@/assets/om-oss-image.png';

const BlogStory = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[80vh] bg-cover bg-center bg-no-repeat flex items-center justify-center"
        style={{ backgroundImage: `url(${omOssImage})` }}>
        <div className="absolute inset-0 bg-background/90" />
        <div className="relative z-10 text-center space-y-8 px-4 max-w-5xl">
          <Badge variant="outline" className="text-sm px-3 py-1">
            GRUNNLEGGERHISTORIE
          </Badge>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold gradient-text leading-tight">
            Fra geologstudent til bransjeopprører
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Historien om hvordan en treningsnerd fra Førde startet en revolusjon mot underdoserte og overprisede kosttilskudd
          </p>
          
          {/* Author info */}
          <div className="flex items-center justify-center space-x-6 text-muted-foreground">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>Jesper Hammersvik</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>8 min lesning</span>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Opening Hook */}
        <Card className="p-8 mb-16 border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-transparent">
          <blockquote className="text-xl md:text-2xl italic text-foreground leading-relaxed">
            "Familien som først tilbød penger for å stoppe prosjektet, ber nå om rabattkode."
          </blockquote>
          <p className="text-muted-foreground mt-4">
            – Dette sitatet oppsummerer reisen til PEVEO på en nøtteskall. Men hvordan kom vi hit?
          </p>
        </Card>

        {/* Story Content */}
        <article className="prose prose-lg max-w-none space-y-12">
          
          {/* Chapter 1: The Beginning */}
          <section className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">1</div>
              <h2 className="text-3xl font-bold">Frustrasjonen som startet det hele</h2>
            </div>
            
            <p className="text-lg leading-relaxed">
              Det var 2022. <strong>Jesper Hammersvik</strong> – en treningsnerd med geologhjerne – sto igjen en gang i treningssenteret 
              og stirret på PWO-boksen han nettopp hadde betalt overpris for. Igjen. Underdoserte ingredienser, 
              oppblåste påstander, og en pris som føltes som ren utpressing.
            </p>
            
            <Card className="p-6 bg-secondary/10">
              <p className="text-lg italic">
                "Jeg var lei av å betale premium for produkter som ikke leverte premium resultater. 
                Ambisjonen ble like enkel som kompromissløs: lage produkter vi selv kan stå hundre prosent inne for."
              </p>
            </Card>
            
            <p className="text-lg leading-relaxed">
              Det som startet som et prosjekt for Jesper og gjengen, skulle snart bli noe mye større. 
              Dokumentert effekt, maksimal dosering, og en pris som faktisk ga mening – dette var 
              ikke bare ønsketenkning, det ble handlingsplanen.
            </p>
          </section>

          {/* Chapter 2: The Launch */}
          <section className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">2</div>
              <h2 className="text-3xl font-bold">Mai 2023: Den første PWO-en som endret alt</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-lg leading-relaxed">
                  Planen var beskjeden: selge tusen bokser på tre år mens Jesper studerte. 
                  Men ryktet spredte seg fort. Spenst Førde, kompiser fra Forsvaret, 
                  og en sak i Firda ga ekstra fart.
                </p>
                
                <p className="text-lg leading-relaxed mt-4">
                  Fasiten? <strong>Utsolgt til jul</strong> – to år på rad.
                </p>
              </div>
              
              <Card className="p-6">
                <div className="space-y-4">
                  <TrendingUp className="w-12 h-12 text-primary" />
                  <h3 className="text-xl font-bold">Eksplosiv vekst</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Planlagt salg (3 år)</span>
                      <span>1,000 bokser</span>
                    </div>
                    <div className="flex justify-between font-bold text-primary">
                      <span>Faktisk salg (7 mnd)</span>
                      <span>1,000+ bokser</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            
            <Card className="p-6 border-l-4 border-l-accent">
              <p className="text-lg italic">
                2024 startet ironisk nok med tomme hyller, men det bekreftet bare poenget: 
                <strong> når du lager noe skikkelig, forsvinner det fort.</strong>
              </p>
            </Card>
          </section>

          {/* Chapter 3: Science First */}
          <section className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">3</div>
              <h2 className="text-3xl font-bold">Vitenskap først, markedsføring etterpå</h2>
            </div>
            
            <p className="text-lg leading-relaxed">
              Mens andre selskaper fokuserer på fancy emballasje og influencer-markedsføring, 
              jobber PEVEO motsatt: <strong>vitenskap først, markedsføring etterpå.</strong>
            </p>
            
            <p className="text-lg leading-relaxed">
              Hvert produkt har solid forskningsgrunnlag bak seg, ikke bare fine ord på emballasjen. 
              Dosene er effektive og virkningsfulle - ikke symbolske mengder som kun ser bra ut på ingredienslisten. 
              Produksjonen skjer i Sverige med batcher som er testet, verifisert og dopingfrie. 
              Vi garanterer Norges beste pris på sammenlignbare produkter, og vi tåler sammenligning 
              med hvem som helst. Sjekk gjerne andre nettsider - vi er ikke redde for konkurranse.
            </p>
          </section>

          {/* Chapter 4: The Taste Revolution */}
          <section className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">4</div>
              <h2 className="text-3xl font-bold">Smak-revolusjonen: 200+ mennesker kan ikke ta feil</h2>
            </div>
            
            <p className="text-lg leading-relaxed">
              Smak betyr faktisk noe. Men i stedet for å gjette, gjennomførte PEVEO noe ingen andre har gjort: 
              en blindtest med <strong>over 200 deltakere</strong> hvor rekkefølgen ble randomisert for hver person.
            </p>
            
            <Card className="p-8 bg-gradient-to-r from-primary/10 to-secondary/10">
              <h3 className="text-2xl font-bold mb-6 text-center">Blindtest-resultater som snakker for seg selv</h3>
              <p className="text-lg leading-relaxed mb-6">
                Tre citrus-varianter, tre tropiske, én fruit punch, én sour apple og noen bonussmaker gikk i mikseren. 
                Rating ble gitt på en skala fra 1 til 10, og deltakerne kunne justere vurderingene sine underveis 
                hvis de ombestemte seg.
              </p>
              
              <p className="text-lg leading-relaxed">
                Resultatet var krystallklart: <strong>citrus vant overlegent med snitt rundt 8,3 til 8,6</strong>. 
                Tropisk fulgte på 7,5 til 7,8, mens fruit punch landet på 7,1 og sour apple stoppet på 6,4. 
                Signifikansen var statistisk overbevisende med p mindre enn 0,01 - det betyr at sjansen for at 
                dette var tilfeldig er mindre enn 1 prosent.
              </p>
            </Card>
            
            <p className="text-lg leading-relaxed">
              Derfor satser PEVEO på én smak - den flest faktisk liker best, ikke den som høres mest eksotisk ut 
              på papiret. Dette gir bedre opplevelse for kundene, færre kompromisser i produksjonen, og 
              stordriftsfordeler som igjen gir markedets beste pris. Vi nekter å tro at noen andre har lagt ned 
              mer arbeid i å perfeksjonere én enkelt smak enn det vi har gjort.
            </p>
          </section>

          {/* Chapter 5: More Than Profit */}
          <section className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">5</div>
              <h2 className="text-3xl font-bold">Mer enn bare profitt</h2>
            </div>
            
            <p className="text-lg leading-relaxed">
              PEVEO er også et <strong>verdiprosjekt</strong>. Overskuddet går til løsninger som hjelper folk 
              med kneplager og bevegelsesutfordringer. Hver boks bidrar til noe større.
            </p>
            
            <Card className="p-6 bg-accent/10">
              <p className="text-lg italic">
                "For å maksimere farten i utviklingen har grunnleggeren til nå valgt å ikke ta ut lønn. 
                Alt reinvesteres i produktforbedringer, patenter og prototyper."
              </p>
            </Card>
          </section>

          {/* Chapter 6: Innovation */}
          <section className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">6</div>
              <h2 className="text-3xl font-bold">Patent-innovasjon: Vi fikser irritasjoner</h2>
            </div>
            
            <p className="text-lg leading-relaxed">
              Vi bygger ikke gimmicker, vi fikser irritasjoner. PEVEO utvikler en todelt patentinnovasjon som faktisk løser reelle problemer folk har med kosttilskudd i dag. 
              Den første innovasjonen er "scoop under lokket" - så du slipper å grave som en arkeolog i pulveret hver gang 
              du skal ha deg en dose. Den andre er "glycerol i lokket", fordi dagens PWO-er ofte underdoserer glycerol så grovt 
              at det blir meningsløst. Målet er en integrert løsning som gir riktig mengde - enkelt, rent og praktisk - 
              og som fungerer i hverdagen, ikke bare på tegnebrettet.
            </p>
            
            <p className="text-lg leading-relaxed">
              Begge disse løsningene kommer fra erfaringer Jesper og teamet selv hadde som brukere. 
              De visste hvor irriterende det var å måtte lete etter skjeen, og hvor sjelden man faktisk fikk 
              i seg nok glycerol til at det gjorde en forskjell.
            </p>
            
            <Card className="p-6 border-l-4 border-l-primary">
              <p className="text-lg">
                <strong>Den ærlige biten:</strong> Dette er dyrt. Avstøpningsformer, verktøy og design koster. 
                For å få produksjonen opp må vi lage minst ti tusen lokk. Hver gang du velger oss, 
                hjelper du denne løsningen bli virkelighet.
              </p>
            </Card>
          </section>

          {/* Chapter 7: The Future */}
          <section className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">7</div>
              <h2 className="text-3xl font-bold">Fremtiden: Fra PEVEO til Stoad</h2>
            </div>
            
            <p className="text-lg leading-relaxed">
              Stoad representerer neste skritt i PEVEO sin reise: treningsapparater med justerbar hoftefleksjon, 
              spesialdesignet CAM-system som matcher faktiske styrkeprofiler, og kapasitet på hele 240 kg. 
              Målet er bedre aktivering av quadriceps, spesielt rectus femoris, for både prestasjon og rehabilitering. 
              Veien fra "nice pump" til "bedre knær" er faktisk ganske kort når teknologien er riktig designet.
            </p>
            
            <p className="text-lg leading-relaxed">
              Stoad er designet for prestasjonsorienterte utøvere som vil maksimere muskelaktiveringen, 
              rehabiliteringssentre som trenger presise og justerbare løsninger, treningssentre og PT-klinikker 
              som vil tilby medlemmene noe unikt, samt fysioterapeuter som jobber med knerehabilitering og 
              styrketrening etter skade.
            </p>
            
            <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5">
              <h3 className="text-2xl font-bold mb-4">Filosofien som driver alt</h3>
              <p className="text-xl leading-relaxed">
                Filosofien er enkel og kompromissløs: færre, bedre produkter. PEVEO har få produkter, 
                og det kommer de alltid til å ha. Helt ærlig - mye av kosttilskudd der ute gir liten 
                eller ingen effekt, og det styrer de unna. De selger bare det som har solid vitenskapelig 
                dokumentasjon og et tydelig formål. På hver produktside finner du en forståelig, 
                vitenskapelig gjennomgang av hvorfor produktet virker, komplett med kilder og direkte 
                lenker til de faktiske studiene.
              </p>
              
              <p className="text-xl leading-relaxed mt-4">
                Logistikken er like målbevisst som produktutviklingen. Med robotlager går alt automatisk 
                i det øyeblikket du bestiller: varen plukkes, pakkes og frakten bestilles på sekunder. 
                Bestill før 14:00 og pakken sendes samme dag, ellers tidlig neste virkedag.
              </p>
            </Card>
          </section>

          {/* Final CTA */}
          <section className="space-y-8 pt-12">
            <Card className="p-8 text-center bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10">
              <h2 className="text-3xl font-bold mb-6">Fremtiden bygges sammen med deg</h2>
              <p className="text-xl mb-8 text-muted-foreground">
                Dette er starten, ikke målet. Vi inviterer deg til å følge reisen videre – 
                der vitenskap møter innovasjon, og pris møter prestasjon.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-lg px-8">
                  Prøv produktene våre
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8">
                  Bli forhandler
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground mt-6">
                Send Jesper Hammersvik en melding på Instagram for personlig oppfølging
              </p>
            </Card>
          </section>
        </article>
      </div>
    </div>
  );
};

export default BlogStory;